import type { FC } from 'react';
import type { BeneficialOwnerResponse, IANATimezones } from '@gcv/shared';
import { BeneficialOwnerUpdateAction } from '@gcv/shared';
import { Box } from '@mui/material';

import { Label, Text } from 'ui';
import { getMostRecentCertification } from 'ui/apps/util/beneficial-owners';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';

interface Props {
  owner: BeneficialOwnerResponse;
  timezone: IANATimezones;
}

export const BeneficialOwnerDrawerUpdateBlurb: FC<Props> = ({ owner, timezone }) => {
  const mostRecentCertification = getMostRecentCertification(owner);

  const formatDate = (date?: string) =>
    date ? DateTimeHelpers.formatISOToFriendlyDateString(date, timezone) : '--';
  const lastCertifiedText = mostRecentCertification
    ? `last certified ${formatDate(mostRecentCertification.date)}`
    : '';

  const status = owner.archived
    ? {
        backgroundColor: palette.background.secondary,
        borderColor: palette.text.secondary,
        label: 'ARCHIVED',
        message: `Archived by ${owner.archived_by_display_name ?? '--'} on ${formatDate(
          owner.archived_date
        )}${lastCertifiedText ? ` · ${lastCertifiedText}` : ''}`
      }
    : owner.last_update.update_action === BeneficialOwnerUpdateAction.Certify
    ? {
        backgroundColor: palette.success.background,
        borderColor: palette.success.main,
        label: 'CERTIFIED',
        message: `${formatDate(owner.last_update.date)} via ${
          owner.last_update.template_result_name
        } questionnaire`
      }
    : mostRecentCertification
    ? {
        backgroundColor: palette.warning.background,
        borderColor: palette.warning.main,
        label: 'UPDATED SINCE CERTIFICATION',
        message: `Last certified ${formatDate(mostRecentCertification.date)} · updated by ${
          owner.last_update.by_display_name ?? '--'
        } on ${formatDate(owner.last_update.date)}`
      }
    : {
        backgroundColor: palette.background.secondary,
        borderColor: palette.text.disabled,
        label: 'NOT YET CERTIFIED',
        message: `${
          owner.last_update.update_action === BeneficialOwnerUpdateAction.Add ? 'Added' : 'Updated'
        } by ${owner.last_update.by_display_name ?? '--'} on ${formatDate(owner.last_update.date)}`
      };

  return (
    <Box
      sx={{
        backgroundColor: status.backgroundColor,
        border: `1px solid ${status.borderColor}`,
        borderRadius: '6px',
        display: 'grid',
        gap: 1,
        p: 2
      }}
    >
      <Label content={status.label} type="subtitle2" sx={{ fontWeight: '700' }} />
      <Text content={status.message} type="body3" />
    </Box>
  );
};
